import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, SquarePen } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SearchableSelect } from "@/components/searchable-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ListPageTable, TablePagination, usePagination } from "@/components/list-controls";
import { feeScheduleStandardQuery } from "@/components/fee-schedules/queries";
import { formatDate } from "@/lib/utils";
import { messageOf } from "@/lib/notify";
import { asPricingMode } from "@/lib/pricing";
import { FREQ_LABEL, type BillingFrequency } from "./services";

/**
 * What one client group pays — read only.
 *
 * A group's fees *are* its fee schedule: attaching a schedule to a group is
 * saying that every company in it bills at these rates, so there is nothing here
 * that belongs to the group alone to edit. This page therefore reports, and the
 * schedule is edited in the one place it lives, `/fee-schedules/{id}`.
 *
 * It used to edit the schedule's newest dated version in place, and offer to
 * attach or swap the schedule from a dialog. Both were the same mistake in two
 * forms: a grid here wrote prices that other groups and companies on the same
 * schedule were also billed at, without saying so, and a schedule swapped here
 * silently reprices every company in the group. Attaching is done from the
 * schedule's own page, which lists the groups reading it and so shows what else
 * a change would reach.
 */
export const Route = createFileRoute("/_authenticated/client-groups_/$groupId_/pricing")({
  head: () => ({
    meta: [
      { title: "Group Pricing — Somasi Ops" },
      {
        name: "description",
        content: "The fee schedule every company in this client group bills at.",
      },
    ],
  }),
  component: GroupPricingPage,
});

type Group = {
  id: string;
  name: string;
  fee_schedule_id: string | null;
};
type ServiceRow = {
  id: string;
  code: string;
  name: string;
  category_id: string | null;
  default_fee: number;
  billing_frequency: BillingFrequency;
};
type ServiceCategory = {
  id: string;
  name: string;
  sort_order: number;
};
/**
 * One price in the group schedule's newest dated set.
 *
 * Keyed by service rather than by an id: a price is identified by the schedule, the
 * date and the service it prices, so there is no separate row id to hold.
 */
type Line = {
  service_id: string;
  standard_fee: number;
  special_fee: number | null;
  billing_frequency: BillingFrequency | null;
};

const money = (n: number) => `USD ${Number(n ?? 0).toFixed(2)}`;

/**
 * The filter value standing for "no category". Not an empty string, which the
 * Select treats as no selection at all and renders as a blank trigger.
 */
const UNCATEGORISED = "__uncategorised";

function GroupPricingPage() {
  const { groupId } = useParams({ from: "/_authenticated/client-groups_/$groupId_/pricing" });
  const [catFilter, setCatFilter] = useState<string>("all");
  // Narrowing the filter makes the old page number meaningless — page 4 of a set
  // that now has one page — so changing it starts again from the top.
  // 20 rather than the default 10: a price list is read down, and the size has to
  // be one of `PAGE_SIZE_OPTIONS` or the footer's "rows per page" select has no
  // item matching its value and renders blank.
  const pager = usePagination({ resetKey: catFilter, pageSize: 20 });

  const { data: group } = useQuery({
    queryKey: ["client_group", groupId],
    queryFn: async () => {
      const { data, error } = await api.clientGroups.get(groupId, [
        "id",
        "name",
        "fee_schedule_id",
      ]);
      if (error) throw error;
      return data as Group;
    },
  });

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["services_active"],
    queryFn: async () =>
      ((
        await api.services
          .list()
          .all()
          .select("id, code, name, category_id, default_fee, billing_frequency, active")
          .eq("active", true)
          .order("code")
      ).data ?? []) as ServiceRow[],
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["service_categories"],
    queryFn: async () =>
      ((await api.serviceCategories.list().order("sort_order").all()).data ??
        []) as ServiceCategory[],
  });

  const scheduleId = group?.fee_schedule_id ?? null;

  const { data: schedule } = useQuery({
    queryKey: ["fee_schedule", scheduleId],
    enabled: !!scheduleId,
    queryFn: async () => (await api.feeSchedules.get(scheduleId!, ["id", "name", "active"])).data,
  });

  // Prices belong to a dated version of the schedule. What is shown is the newest
  // one — "what this group pays now" — and never an earlier version, whose prices
  // are what past invoices were raised at. The schedule's own page is where the
  // earlier ones can be read.
  const { data: versions = [] } = useQuery({
    queryKey: ["fee_schedule_versions", scheduleId],
    enabled: !!scheduleId,
    queryFn: async () => (await api.feeSchedules.versions(scheduleId!)).data ?? [],
  });

  const latestDate = versions.find((v) => v.is_latest)?.effective_from ?? null;

  const { data: lines = [] } = useQuery({
    queryKey: ["fee_schedule_prices", scheduleId, latestDate],
    enabled: !!scheduleId && !!latestDate,
    queryFn: async () =>
      ((await api.feeSchedules.prices(scheduleId!, latestDate!)).data ?? []) as Line[],
  });

  /**
   * How many companies in the group these rates actually bill.
   *
   * Only the ones left on `client_group` mode: one moved to `company_specific`
   * keeps its own fees and is unaffected, which is worth saying out loud on a
   * page that otherwise reads as the whole group's price list.
   *
   * The mode is resolved per row rather than filtered on, because a row written
   * before the column existed has none and `asPricingMode` infers it — so the
   * three columns it reads are projected and nothing else is. This is a count,
   * not a list, since the page no longer names the companies.
   */
  const { data: companies = [] } = useQuery({
    queryKey: ["companies_in_group_modes", groupId],
    queryFn: async () =>
      ((
        await api.companies
          .list()
          .all()
          .select("id, pricing_mode, fee_schedule_id, client_group_id")
          .eq("client_group_id", groupId)
      ).data ?? []) as {
        id: string;
        pricing_mode: string;
        fee_schedule_id: string | null;
        client_group_id: string | null;
      }[],
  });

  const following = companies.filter((c) => asPricingMode(c.pricing_mode, c) === "client_group");

  /**
   * The Somasi standard price list, and what it bills today.
   *
   * The reference column on this page is "what Somasi charges as standard", and that
   * number belongs to the schedule carrying `is_standard` — found by its flag, at the
   * version in force today, both of which are server decisions. It is not read off
   * each service row: `services.default_fee` is a copy of the standard rate with no
   * history, so a new version of the standard list moves every service's standard fee
   * at once and leaves the stored copies behind. Comparing a dated group rate against
   * an undated copy is how a group looked discounted against a price nobody charges.
   *
   * One request for the whole catalogue, however many rows are on screen — the rates
   * are one dated version of one schedule.
   */
  const { data: standard, error: standardError } = useQuery(feeScheduleStandardQuery());

  const standardFees = useMemo(
    () => new Map((standard?.prices ?? []).map((price) => [price.service_id, price.fee])),
    [standard],
  );

  /**
   * What one service charges as standard.
   *
   * The standard list's rate where it prices the service; the service's own stored fee
   * where it does not — which is what the price cascade falls back to, and so what a
   * company on this group would actually be billed for a service neither list prices.
   */
  const standardFee = (service: ServiceRow) =>
    standardFees.get(service.id) ?? Number(service.default_fee ?? 0);

  const lineByService = useMemo(() => {
    const m = new Map<string, Line>();
    lines.forEach((l) => m.set(l.service_id, l));
    return m;
  }, [lines]);

  const categoryName = (id: string | null) =>
    (id && categories.find((c) => c.id === id)?.name) || "Uncategorised";

  /**
   * The rows the table shows, narrowed by the category filter.
   *
   * Filtered here rather than at the API, which is the opposite of how
   * `/services` does it — and for a reason. This page already holds every active
   * service, because the catalogue fee of each one is part of what it reports, so
   * re-asking the server for a subset would be a round trip to discard rows
   * already in hand. It also keeps the totals in the summary line honest: those
   * count the whole catalogue, not whatever the filter is currently showing.
   *
   * "Uncategorised" covers a service whose category is unset *and* one pointing
   * at a category the catalogue no longer lists, so no service can be hidden from
   * every setting of the filter.
   */
  const visible = useMemo(() => {
    if (catFilter === "all") return services;
    if (catFilter === UNCATEGORISED) {
      const live = new Set(categories.map((c) => c.id));
      return services.filter((s) => !s.category_id || !live.has(s.category_id));
    }
    return services.filter((s) => s.category_id === catFilter);
  }, [services, categories, catFilter]);

  /** Only offered when something is actually in it, so the filter cannot go blank. */
  const hasUncategorised = useMemo(() => {
    const live = new Set(categories.map((c) => c.id));
    return services.some((s) => !s.category_id || !live.has(s.category_id));
  }, [services, categories]);

  /**
   * The rows on screen.
   *
   * Sliced here rather than asked for by page, for the same reason the filter is
   * applied here: the whole active catalogue is already in hand. `TablePagination`
   * is handed `visible.length` as the total, so the footer counts what the filter
   * matched rather than the catalogue — and it corrects a page number left past
   * the end, which is what happens when a narrower filter shortens the set.
   */
  const pageRows = useMemo(
    () => visible.slice(pager.offset, pager.offset + pager.pageSize),
    [visible, pager.offset, pager.pageSize],
  );

  /** The categories worth offering: the ones holding an active service. */
  const offered = useMemo(() => {
    const used = new Set(services.map((s) => s.category_id ?? ""));
    return categories.filter((c) => used.has(c.id));
  }, [services, categories]);

  if (!group) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const priced = lines.length;

  return (
    // Fills the window like a list page; the services scroll inside the table.
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title={`Group Pricing · ${group.name}`}
        description="What every company in this group bills at. The rates belong to the fee schedule, and are changed there."
        breadcrumbs={[
          { label: "Client Groups", to: "/client-groups" },
          // A real path, not a route pattern: `Crumb.to` is a plain string the
          // shell hands straight to `Link`, with no `params` to interpolate.
          { label: group.name, to: `/client-groups/${groupId}` },
          { label: "Pricing" },
        ]}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/client-groups/$groupId" params={{ groupId }}>
                <ArrowLeft className="mr-2 h-4 w-4" /> {group.name}
              </Link>
            </Button>
            {scheduleId && (
              <Button asChild>
                <Link to="/fee-schedules/$scheduleId" params={{ scheduleId }}>
                  <SquarePen className="mr-2 h-4 w-4" /> Edit fee schedule
                </Link>
              </Button>
            )}
          </>
        }
      />

      {!scheduleId ? (
        <Alert className="mb-4 shrink-0">
          <AlertTitle>No fee schedule attached</AlertTitle>
          <AlertDescription>
            Every company in this group is billed at the standard fee. To price this group, open a{" "}
            <Link to="/fee-schedules" className="underline">
              fee schedule
            </Link>{" "}
            and add this group to it — the schedule's own page lists every group reading it, so it
            shows what else a rate change would reach.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="mb-4 shrink-0">
          <AlertTitle>
            {schedule?.name ?? "Fee schedule"}
            {latestDate && (
              <span className="ml-2 font-normal text-muted-foreground">
                rates in force from {formatDate(latestDate)}
              </span>
            )}
          </AlertTitle>
          <AlertDescription>
            {priced === 0
              ? "This schedule prices nothing yet, so every company in the group bills at the standard fee."
              : `${priced} of ${services.length} services priced for this group; the rest are billed at the standard fee.`}{" "}
            {following.length === 0
              ? "No company in this group is on client-group pricing yet, so nothing here is billed today."
              : `${following.length} compan${following.length === 1 ? "y bills" : "ies bill"} at these rates.`}{" "}
            Other groups and companies may read the same schedule — its own page says which.
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
        <SearchableSelect
          value={catFilter}
          onChange={setCatFilter}
          options={[
            { value: "all", label: "All categories" },
            ...offered.map((c) => ({ value: c.id, label: c.name })),
            ...(hasUncategorised ? [{ value: UNCATEGORISED, label: "Uncategorised" }] : []),
          ]}
          searchPlaceholder="Search categories…"
          emptyText="No category matches that."
          className="w-64"
          contentClassName="w-80 p-0"
        />
        <p className="text-xs text-muted-foreground">
          {visible.filter((s) => lineByService.has(s.id)).length} of {visible.length} priced for
          this group
          {catFilter !== "all" && ` · ${services.length} services in the catalogue`}
        </p>
      </div>

      {/*
       * Where the "Somasi standard" column comes from. Worth saying: the column read
       * the service's own stored fee before, so a reader who remembers that number
       * would otherwise have no way to tell why it moved when a new version of the
       * standard list started.
       */}
      <p className="mb-3 shrink-0 text-xs text-muted-foreground">
        {standardError
          ? `Somasi standard could not be read from the standard price list (${messageOf(standardError)}), so each service shows its own stored fee.`
          : standard
            ? standard.effective_from
              ? `Somasi standard is ${standard.name}'s rate in force today, dated ${formatDate(standard.effective_from)}. A service that list does not price shows its own stored fee.`
              : `${standard.name} is the standard price list but holds no dated prices yet, so every service shows its own stored fee.`
            : "No schedule is marked as the standard price list, so each service shows its own stored fee. Mark one on a schedule's page to price the catalogue from a schedule."}
      </p>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead className="w-56">Category</TableHead>
              <TableHead className="w-44">Frequency</TableHead>
              <TableHead className="w-36 text-right">Somasi standard</TableHead>
              <TableHead className="w-44 text-right">Group fee</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {servicesLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  {services.length === 0
                    ? "No active services in the catalogue."
                    : "No active service in that category."}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((s) => {
                const line = lineByService.get(s.id);
                return (
                  <TableRow key={s.id} className={line ? "" : "opacity-70"}>
                    <TableCell>
                      <div className="font-medium">{s.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{s.code}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {categoryName(s.category_id)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {line?.billing_frequency ? (
                        FREQ_LABEL[line.billing_frequency]
                      ) : (
                        <span className="text-muted-foreground">
                          {FREQ_LABEL[s.billing_frequency]}
                          {line && " (catalogue)"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {money(standardFee(s))}
                    </TableCell>
                    <TableCell className="text-right">
                      {line ? (
                        <span className="font-mono text-xs tabular-nums">
                          {money(line.special_fee ?? line.standard_fee)}
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          standard fee
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          pagination={pager}
          total={visible.length}
          loading={servicesLoading}
          unit="service"
        />
      </ListPageTable>
    </div>
  );
}
